import React, { useMemo, useState, useCallback, useContext, useLayoutEffect } from 'react'
import { Link, useHistory } from 'react-router-dom'
import Modal from '@mui/material/Modal'
import { PurchaseBNZ } from '../../components'
import DashboardContext from '../../contexts/DashboardContext'
import { decodedToken } from '../../services/auth'
import {
  Crown,
  ArrowRight,
  Sparkles,
  Video,
  Zap,
  Shield,
  Clock,
  CheckCircle2,
  AlertCircle,
  Wallet,
  Plus,
  ArrowDownLeft,
  ArrowUpRight,
} from 'lucide-react'
import CustomSpinner from '../CustomSpinner'
import RequestService from '../Modal/BonuzPlans/ResquestService'
import { formatISOTimestampToDate } from '../../utils/formatDate'
import '../DashboardBonuzEssencialHome/dashboard-bonuz-essencial-home.css'
import './dashboard-bonuz-start-home.css'

const REVISIA_URL = 'https://revisia.bonuz.it/auth/bonuz/redirect'

function demandStatusLabel(status) {
  const map = {
    pending: 'Pendente',
    in_progress: 'Em andamento',
    stand_by: 'Stand-by',
    waiting_approval: 'Aguardando aprovação',
    in_estimating: 'Aguardando orçamento',
    draft: 'Pendente de informação',
    demand_sent: 'Demanda enviada',
    in_review: 'Aguardando revisão',
    canceled: 'Cancelado',
    done: 'Concluído',
    waiting_estimate_approval: 'Aguardando aprovação do orçamento',
    waiting_lawyer_acceptance: 'Aguardando aceite do advogado',
  }
  return map[status] || 'Em andamento'
}

function statusIconClass(status) {
  switch (status) {
    case 'done':
    case 'demand_sent':
      return 'green'
    case 'pending':
    case 'draft':
    case 'waiting_approval':
    case 'in_review':
    case 'in_estimating':
    case 'waiting_estimate_approval':
    case 'waiting_lawyer_acceptance':
      return 'amber'
    case 'in_progress':
      return 'blue'
    default:
      return 'gray'
  }
}

function StatusGlyph({ status }) {
  const cls = statusIconClass(status)
  if (status === 'done' || status === 'demand_sent') {
    return <CheckCircle2 size={16} />
  }
  if (cls === 'amber') {
    return <AlertCircle size={16} />
  }
  if (cls === 'blue') {
    return <Clock size={16} />
  }
  return <Clock size={16} />
}

function formatDemandLineDate(demand) {
  if (demand?.deadline) {
    try {
      const { formattedDate } = formatISOTimestampToDate(demand.deadline, 'ISO')
      return `Prazo ${formattedDate}`
    } catch (e) {
      return ''
    }
  }
  if (demand?.finalDate) {
    try {
      const { formattedDate } = formatISOTimestampToDate(demand.finalDate, 'ISO')
      return formattedDate
    } catch (e) {
      return ''
    }
  }
  return ''
}

const DashboardBonuzStartHome = ({
  wallet,
  lastStatements,
  loadingStatements,
  recentDemandsData,
  recentDemandsLoading,
  totalDemandsCount,
  onPurchaseModalClose,
}) => {
  const history = useHistory()
  const [, setPackageId] = useContext(DashboardContext)
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false)
  const [purchaseUnavailable, setPurchaseUnavailable] = useState(false)

  useLayoutEffect(() => {
    const jwtSettings = decodedToken()
    if (jwtSettings?.adminId && jwtSettings?.adminAsUser) {
      setPurchaseUnavailable(true)
    }
  }, [])

  const closePurchaseModal = useCallback(() => {
    setPurchaseModalOpen(false)
    setPackageId('false')
    if (onPurchaseModalClose) {
      onPurchaseModalClose()
    }
  }, [setPackageId, onPurchaseModalClose])

  const openPurchaseModal = useCallback(() => {
    if (!purchaseUnavailable) {
      setPurchaseModalOpen(true)
    }
  }, [purchaseUnavailable])

  const [openModalRequestService, setOpenModalRequestService] = useState(false)
  const modalOpenRequestService = useCallback(() => {
    setOpenModalRequestService((v) => !v)
  }, [])

  const planCredits = wallet?.creditsBonuzPlans ?? 0
  const adquiridos = wallet?.saldoFinal ?? wallet?.balance ?? 0

  const demandRows = useMemo(() => {
    const raw = recentDemandsData?.listDemands?.data || []
    const active = raw.filter((d) => d.status !== 'done' && d.status !== 'canceled')
    return active.slice(0, 5)
  }, [recentDemandsData])

  const statements = lastStatements || []

  return (
    <div className="dbe-root dbs-start-root">
      <Modal
        open={purchaseModalOpen}
        onClose={closePurchaseModal}
        disableAutoFocus
        sx={{
          zIndex: '9999',
          '& .MuiBackdrop-root': {
            backgroundColor: 'var(--overlay-bg)',
            transition: 'background-color 0.3s ease',
          },
        }}
      >
        <PurchaseBNZ closeParentModal={closePurchaseModal} />
      </Modal>
      <RequestService
        openModal={openModalRequestService}
        handleClose={modalOpenRequestService}
        requestedService={false}
      />

      <div className="dbe-stack">
        <div className="dbs-main-grid">
          <div className="dbs-col-main dbe-stack">
            {/* ServiceRequest — Legal Manager / Premium */}
            <div className="dbe-card dbs-service-request">
              <div className="dbs-service-request-inner">
                <div className="dbs-service-request-row">
                  <div className="dbs-service-icon" aria-hidden>
                    <Crown size={28} />
                  </div>
                  <div className="dbs-service-body">
                    <div className="dbs-service-head">
                      <h3 className="dbs-service-title">Tenha um Legal Manager dedicado</h3>
                      <span className="dbs-badge-premium">
                        <Sparkles size={12} />
                        Premium
                      </span>
                    </div>
                    <p className="dbs-service-desc">
                      Com o Plano Premium, você tem um advogado especializado para atender suas demandas com
                      prioridade e SLA de 2 dias. Assessoria jurídica personalizada quando você precisar.
                    </p>
                    <button type="button" className="dbe-btn-primary dbs-service-cta" onClick={modalOpenRequestService}>
                      Fazer upgrade para Premium
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* QuickActionsHighlight */}
            <div className="dbe-quick-grid">
              <div className="dbe-quick-card">
                <div className="dbe-quick-row">
                  <div className="dbe-icon-box">
                    <Video size={24} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3>Fale com advogado</h3>
                    <p>Consultoria de 30 min por videochamada</p>
                    <Link to="/dashboard/fala-advogado" className="dbe-btn-outline">
                      Agendar consulta
                    </Link>
                  </div>
                </div>
              </div>
              <div className="dbe-quick-card accent-border">
                <div className="dbe-quick-row">
                  <div className="dbe-icon-box filled">
                    <Sparkles size={24} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="dbe-quick-head">
                      <h3 style={{ marginBottom: 0 }}>Revisia</h3>
                      <span className="dbe-badge-new">Novo!</span>
                    </div>
                    <p>Análise inteligente de documentos com IA</p>
                    <a href={REVISIA_URL} target="_blank" rel="noopener noreferrer" className="dbe-btn-solid">
                      Experimentar agora
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* PlanUpgrade */}
            <div className="dbs-plan-upgrade-grid">
              <div className="dbe-quick-card dbs-plan-upgrade-card">
                <div className="dbe-quick-row">
                  <div className="dbe-icon-box">
                    <Zap size={24} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="dbe-quick-head">
                      <h3 style={{ marginBottom: 0 }}>Plano Essencial</h3>
                      <span className="dbs-badge-outline">Upgrade</span>
                    </div>
                    <p>1350 BNZs/mês + 10% desconto em compras</p>
                    <button type="button" className="dbe-btn-outline" onClick={modalOpenRequestService}>
                      Fazer upgrade
                    </button>
                  </div>
                </div>
              </div>
              <div className="dbe-quick-card accent-border dbs-plan-upgrade-card">
                <div className="dbe-quick-row">
                  <div className="dbe-icon-box">
                    <Crown size={24} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="dbe-quick-head">
                      <h3 style={{ marginBottom: 0 }}>Plano Premium</h3>
                      <span className="dbs-badge-outline accent">VIP</span>
                    </div>
                    <p>Legal Manager + SLA 2 dias</p>
                    <button type="button" className="dbe-btn-primary dbs-inline-btn" onClick={modalOpenRequestService}>
                      Contratar Legal Manager
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* DPOService */}
            <div className="dbe-card dbs-dpo-card">
              <div className="dbe-card-pad">
                <div className="dbe-quick-row">
                  <div className="dbe-icon-box filled">
                    <Shield size={24} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="dbe-quick-head">
                      <h3 style={{ marginBottom: 0 }} className="dbe-card-title">
                        DPO as a Service
                      </h3>
                      <span className="dbs-badge-dpo">Data Privacy</span>
                    </div>
                    <p className="dbe-card-muted" style={{ marginBottom: '0.75rem' }}>
                      Compliance LGPD com DPO dedicado e gestão completa de privacidade
                    </p>
                    <button type="button" className="dbe-btn-outline" onClick={modalOpenRequestService}>
                      Conhecer plano
                      <ArrowRight size={14} style={{ marginLeft: 4 }} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ActiveDemands */}
            <div className="dbe-card">
              <div className="dbe-demands-header">
                <h3>Demandas ativas</h3>
                <span className="dbe-badge-count">{totalDemandsCount} total</span>
              </div>
              {recentDemandsLoading && (
                <div className="dbe-spinner-wrap">
                  <CustomSpinner />
                </div>
              )}
              {!recentDemandsLoading && demandRows.length === 0 && (
                <div className="dbe-empty">Você ainda não possui demandas ativas.</div>
              )}
              {!recentDemandsLoading &&
                demandRows.map((demand) => {
                  const st = statusIconClass(demand.status)
                  return (
                    <div
                      key={demand._id}
                      className="dbe-demand-row"
                      role="button"
                      tabIndex={0}
                      onClick={() => history.push(`/dashboard/serviços/detalhes/${demand._id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') history.push(`/dashboard/serviços/detalhes/${demand._id}`)
                      }}
                    >
                      <div className="dbe-demand-left">
                        <div className={`dbe-status-icon ${st}`}>
                          <StatusGlyph status={demand.status} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <p className="dbe-demand-title" title={demand.fullDescription || demand.code}>
                            {(demand.fullDescription || demand.code || '').slice(0, 80)}
                            {(demand.fullDescription || '').length > 80 ? '…' : ''}
                          </p>
                          <div className="dbe-demand-meta">
                            <span className="dbe-chip">{demandStatusLabel(demand.status)}</span>
                            {formatDemandLineDate(demand) && (
                              <span className="dbe-demand-date">{formatDemandLineDate(demand)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="dbe-demand-credits">
                        <strong>{demand.price != null ? demand.price : '—'}</strong>
                        <span>BNZs</span>
                      </div>
                    </div>
                  )
                })}
            </div>
          </div>

          <div className="dbs-col-side dbe-stack">
            <div className="dbe-credit-card">
              <div className="dbe-credit-inner">
                <div className="dbe-credit-top">
                  <div className="dbe-credit-label">
                    <Wallet size={16} />
                    <span>Meus créditos</span>
                  </div>
                  <button
                    type="button"
                    className="dbe-credit-buy"
                    onClick={openPurchaseModal}
                    disabled={purchaseUnavailable}
                  >
                    <Plus size={16} />
                    Comprar
                  </button>
                </div>
                <div className="dbe-credit-block">
                  <div className="lbl">Start</div>
                  <div className="dbe-credit-figures">
                    <span className="val">{planCredits}</span>
                    <span className="val-unit">BNZs</span>
                  </div>
                  <div className="lbl2">*seu saldo de BNZs disponíveis</div>
                </div>
                <div className="dbe-sep" />
                <div className="dbe-credit-block">
                  <div className="lbl">Adquiridos</div>
                  <div className="dbe-credit-figures">
                    <span className="val">{adquiridos}</span>
                    <span className="val-unit">BNZs</span>
                  </div>
                  <div className="lbl2">*créditos adicionais em carteira</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RecentTransactions */}
        <div className="dbe-card">
          <div className="dbe-trans-header">
            <h3>Transações recentes</h3>
          </div>
          {loadingStatements && (
            <div className="dbe-spinner-wrap">
              <CustomSpinner />
            </div>
          )}
          {!loadingStatements && (!statements || statements.length === 0) && (
            <div className="dbe-empty">Você não fez nenhuma transação recente.</div>
          )}
          {!loadingStatements &&
            statements.map((item) => {
              const isCredit =
                item.type === 'credit' ||
                (item.type !== 'debit' && item.type !== 'reserve' && parseFloat(item.amount) > 0)
              const amt = Math.abs(parseFloat(item.amount))
              const { formattedDate, formattedHourMinute } = formatISOTimestampToDate(item.createdAt, 'ISO')
              return (
                <div key={item._id} className="dbe-trans-row">
                  <div className="dbe-trans-left">
                    <div className={`dbe-trans-ico ${isCredit ? 'credit' : 'debit'}`}>
                      {isCredit ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
                    </div>
                    <div>
                      <p className="dbe-trans-title">{item.description}</p>
                      <p className="dbe-trans-sub">
                        {formattedDate} {formattedHourMinute} • Transação em BNZs
                      </p>
                    </div>
                  </div>
                  <p className={`dbe-trans-amt ${isCredit ? 'credit' : 'debit'}`}>
                    {isCredit ? '+' : '-'}
                    {amt.toLocaleString('pt-BR')} BNZs
                  </p>
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}

export default DashboardBonuzStartHome
